import { useCallback, useState, ChangeEvent } from 'react';
import { useTranslations } from 'next-intl';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { Heading } from '@app/app/_components/Heading/Heading';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { SideSheet } from '@app/app/_components/SideSheet/SideSheet';
import { ArrowLeftIcon } from '@app/app/_icons/ArrowLeftIcon';
import { Button } from '@app/app/_components/Button/Button';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { CheckThinIcon } from '@app/app/_icons/CheckThinIcon';
import { formatAddress } from '@app/lib/address-formatter/formatAddress';
import { Address } from '@app/domain/model/Address';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseHomeInsuranceResult } from '@app/service/eshop/home/actions/purchaseHomeInsurance';
import { PolicyholderFormFields } from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/hooks/usePolicyholderFormFields';
import { PolicyholderInformationStep } from '@app/app/esales/home/_sections/steps/PolicyholderInformationStep/PolicyholderInformationStep';
import { Tenant } from '@app/infrastructure/tenant/model/Tenant';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';

interface Props {
    policyholderFields: PolicyholderFormFields;
    formState: FormState<PurchaseHomeInsuranceResult>;
    personalCode: string;
    tenant: Tenant;
    onSave: (data: PolicyholderFormFields) => void;
}

export function PolicyholderSummaryCard({ policyholderFields, formState, personalCode, tenant, onSave }: Props) {
    const t = useTranslations();
    const [sideSheetOpen, setSideSheetOpen] = useState(false);
    const [editFormData, setEditFormData] = useState<PolicyholderFormFields>(policyholderFields);

    const handleFirstNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, firstName: e.target.value }));
    }, []);

    const handleLastNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, lastName: e.target.value }));
    }, []);

    const handleAddressChange = useCallback((address: Nullable<Address>) => {
        setEditFormData(prev => ({ ...prev, address }));
    }, []);

    const handlePhoneNumberChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, phoneNumber: e.target.value }));
    }, []);

    const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, email: e.target.value }));
    }, []);

    const handleEmailConfirmationChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setEditFormData(prev => ({ ...prev, emailConfirmation: e.target.value }));
    }, []);

    const editHandlers = {
        handleFirstNameChange,
        handleLastNameChange,
        handleAddressChange,
        handlePhoneNumberChange,
        handleEmailChange,
        handleEmailConfirmationChange,
    };

    const handleOpenSideSheet = useCallback(() => {
        setEditFormData({
            ...policyholderFields,
            address: policyholderFields.address ? { ...policyholderFields.address } : null,
        });
        setSideSheetOpen(true);
    }, [policyholderFields]);

    const handleCloseSideSheet = () => {
        setSideSheetOpen(false);
    };

    const handleCancel = () => {
        handleCloseSideSheet();
    };

    const handleSave = () => {
        onSave(editFormData);
        handleCloseSideSheet();
    };

    const sideSheetHeader = (
        <div className="flex items-center justify-between rounded-t-xl border-b border-neutral-300 px-6 py-4">
            <div className="flex items-center gap-3">
                <GhostButton className="cursor-pointer" onClick={handleCloseSideSheet}>
                    <ArrowLeftIcon className="size-5 text-neutral-500" />
                </GhostButton>
                <Heading level="h3">{t('eshop.home.summary.editPolicyholderInformation')}</Heading>
            </div>
        </div>
    );

    const sideSheetFooter = (
        <div className="flex h-min w-full items-center justify-between gap-6 bg-white px-6 py-4 shadow-2xl">
            <Button
                variant="outlined"
                className="w-full"
                rightAddon={<CloseIcon className="size-4" />}
                onClick={handleCancel}>
                {t('general.cancel')}
            </Button>
            <Button className="w-full" rightAddon={<CheckThinIcon className="size-5" />} onClick={handleSave}>
                {t('general.save')}
            </Button>
        </div>
    );

    return (
        <>
            <CollapsablePanel
                label={`${policyholderFields.firstName} ${policyholderFields.lastName}`}
                collapsable={false}
                addon={<TextButton onClick={handleOpenSideSheet}>{t('general.edit')}</TextButton>}>
                <div className="p-6">
                    <div className="flex flex-col gap-2 border-b border-neutral-150 pb-4">
                        <Heading level="h4">{t('eshop.home.summary.personalInformation')}</Heading>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            <ReadOnlyField label={t('eshop.home.summary.nameLabel')}>
                                {policyholderFields.firstName}
                            </ReadOnlyField>
                            <ReadOnlyField label={t('eshop.home.summary.surnameLabel')}>
                                {policyholderFields.lastName}
                            </ReadOnlyField>
                            <ReadOnlyField label={t('eshop.home.summary.personalCodeDisplayLabel')}>
                                {personalCode}
                            </ReadOnlyField>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 pt-4">
                        <Heading level="h4">{t('eshop.home.summary.contactInformation')}</Heading>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <ReadOnlyField className="sm:col-span-2" label={t('eshop.home.summary.addressLabel')}>
                                {policyholderFields.address ? formatAddress(policyholderFields.address) : ''}
                            </ReadOnlyField>
                            <ReadOnlyField label={t('eshop.home.summary.phoneLabel')}>
                                {policyholderFields.phoneNumber ? `+370${policyholderFields.phoneNumber}` : ''}
                            </ReadOnlyField>
                            <ReadOnlyField
                                className="max-sm:break-words sm:text-nowrap"
                                label={t('eshop.home.summary.emailLabel')}>
                                {policyholderFields.email}
                            </ReadOnlyField>
                        </div>
                    </div>
                </div>
            </CollapsablePanel>

            <SideSheet open={sideSheetOpen} header={sideSheetHeader} footer={sideSheetFooter} contentClassName="p-6">
                {sideSheetOpen && (
                    <div className="flex flex-col gap-6">
                        <PolicyholderInformationStep
                            policyholderFields={editFormData}
                            policyholderHandlers={editHandlers}
                            formState={formState}
                            personalCode={personalCode}
                            tenant={tenant}
                        />
                    </div>
                )}
            </SideSheet>
        </>
    );
}
